import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getDefaultRoute } from '../../utils/roleUtils';
import { ThemeToggle } from '../ui/ThemeToggle';
import LanguageToggle from '../ui/LanguageToggle';
import ClassTechLogo from '../brand/ClassTechLogo';

const links = [
  { to: '/features', labelKey: 'features' },
  { to: '/pricing', labelKey: 'pricing' },
  { to: '/support', labelKey: 'support' },
  { to: '/about', labelKey: 'about' },
];

const PublicHeader: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation('navigation');
  const [open, setOpen] = useState(false);
  const dashboardPath = getDefaultRoute(user?.role || 'student');

  return (
    <header className='sticky top-0 z-40 border-b border-secondary-200/80 bg-white/95 shadow-[0_1px_2px_rgba(16,42,67,0.04)] backdrop-blur-xl dark:border-secondary-800 dark:bg-secondary-950/90'>
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <div className='flex min-h-16 items-center justify-between gap-4'>
          <Link to='/home' className='flex items-center gap-2' aria-label='Class Tech'>
            <ClassTechLogo size='sm' />
          </Link>

          <nav className='hidden items-center gap-1 lg:flex' aria-label={t('publicMain')}>
            {links.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className='rounded-lg px-3 py-2 text-sm font-semibold text-secondary-600 hover:bg-secondary-100 hover:text-secondary-950 dark:text-secondary-300 dark:hover:bg-secondary-800 dark:hover:text-white'
              >
                {t(link.labelKey)}
              </Link>
            ))}
          </nav>

          <div className='flex items-center gap-2'>
            <ThemeToggle variant='minimal' size='md' />
            <LanguageToggle className='hidden md:inline-flex' />
            {user ? (
              <Link
                to={dashboardPath}
                className='hidden min-h-10 items-center rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700 sm:inline-flex'
              >
                {t('dashboard')}
              </Link>
            ) : (
              <>
                <Link
                  to='/login'
                  className='hidden rounded-lg px-3 py-2 text-sm font-semibold text-secondary-700 hover:bg-secondary-100 dark:text-secondary-200 dark:hover:bg-secondary-800 sm:block'
                >
                  {t('login')}
                </Link>
                <Link
                  to='/register'
                  className='hidden min-h-10 items-center rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 sm:inline-flex'
                >
                  {t('register')}
                </Link>
              </>
            )}
            <button
              type='button'
              aria-label={open ? t('closeMenu') : t('openMenu')}
              aria-expanded={open}
              onClick={() => setOpen(value => !value)}
              className='flex h-10 w-10 items-center justify-center rounded-lg text-secondary-600 hover:bg-secondary-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-secondary-300 dark:hover:bg-secondary-800 lg:hidden'
            >
              {open ? <X className='h-5 w-5' /> : <Menu className='h-5 w-5' />}
            </button>
          </div>
        </div>

        {open ? (
          <nav
            className='space-y-1 border-t border-secondary-200 py-3 dark:border-secondary-800 lg:hidden'
            aria-label={t('mobileMenu')}
          >
            <LanguageToggle className='w-full justify-start px-3' />
            {links.map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className='block rounded-lg px-3 py-2 text-sm font-semibold text-secondary-700 hover:bg-secondary-100 dark:text-secondary-200 dark:hover:bg-secondary-800'
              >
                {t(link.labelKey)}
              </Link>
            ))}
            <Link
              to={user ? dashboardPath : '/login'}
              onClick={() => setOpen(false)}
              className='block rounded-lg bg-primary-600 px-3 py-2 text-center text-sm font-semibold text-white'
            >
              {user ? t('dashboard') : t('login')}
            </Link>
            {!user ? (
              <Link
                to='/register'
                onClick={() => setOpen(false)}
                className='block rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 text-center text-sm font-semibold text-primary-700'
              >
                {t('register')}
              </Link>
            ) : null}
          </nav>
        ) : null}
      </div>
    </header>
  );
};

export default PublicHeader;
